import React, { useCallback } from 'react';
import { Image, ImageWithZoom, Slide } from 'pure-react-carousel';

type RenderSlideProps = {
  /** Switch the applied css rules and change the component rendering based on the device. */
  deviceType?: 'desktop' | 'mobile';
  /** The array index number of this slide.  This same number is used in Dot, and thumbnail buttons
  to link them together.  For example, clicking on Dot 3 moves slide 3 into the leftmost
  slot in the Carousel viewport.  See pure-react-carousel documentation for more info.
  This number is generated automatically in Carousel. */
  i: number;

  /** The desired HTML tag for images. Defaults to a div with a background-image style */
  imageTag?: 'div' | 'img';

  /** When true, Pure React Carousel will display a master spinner over the entire carousel until all carousel images have loaded. */
  loading: boolean;

  /**  a unique id for the carousel. This number is used to help create unique css id's and class
  names so that you can have more than one carousel on the page and style them differently,
  or reference a dom element via their unique id attribute name via javascript.
  This number is generated automatically in Carousel and passed in. */
  uid: number;

  /** A url to an large (zoomed in or modal) image.  Passed in by Carousel. */
  large?: string;

  /** A url to a "normal sized" image. Not a thumbnail, and not a large image.
  This is "optional" for backwards compatibility, but as of 11/12/18 we should be using
  this to avoid "fuzzy images" due to the browser down sampling a large image into a small
  space. */
  main?: string;

  /** Is the Carousel component currently showing a modal window.  Passed in by Carousel. */
  isModalOpen: boolean;

  /** The index number of an image from the images array currently visible in the modal.  Passed
  in by Carousel. */
  modalImageIndex: number | null;

  /** Action handler for clicks on the image.  Passed in by Carousel. */
  onClick?: (i: number, ev: React.MouseEvent<HTMLDivElement>) => void;

  /** Action handler for keydown events on the image.  Passed in by Carousel. */
  onKeyDown?: (i: number, ev: React.KeyboardEvent<HTMLDivElement>) => void;

  productName?: string;

  zoom?: boolean;
};

const RenderSlide = ({
  deviceType = 'desktop',
  i,
  imageTag = 'div',
  isModalOpen,
  large,
  loading,
  onClick,
  onKeyDown,
  main,
  modalImageIndex,
  productName,
  uid,
  zoom = true,
}: RenderSlideProps) => {
  const handleOnClick = useCallback(
    (ev: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(i, ev);
    },
    [i, onClick]
  );

  const handleKeyEvent = useCallback(
    (ev: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(i, ev);
    },
    [i, onKeyDown]
  );

  return (
    <Slide
      aria-pressed={isModalOpen && modalImageIndex === i}
      id={`carousel__slide--${uid}--${i}`}
      index={i}
      onClick={handleOnClick}
      onKeyDown={handleKeyEvent}
      className="focus-on-modal-close"
      role="button"
    >
      {deviceType === 'desktop' && zoom ? (
        <ImageWithZoom
          src={main ?? large ?? ''}
          srcZoomed={large}
          bgImageProps={{ hasMasterSpinner: loading }}
          bgImageTag={imageTag}
        />
      ) : (
        <Image
          tag={imageTag}
          src={main ?? large ?? ''}
          hasMasterSpinner={loading}
          alt={productName}
        />
      )}
    </Slide>
  );
};

export default RenderSlide;
